import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-button';
import '@vitesse/base-ui/elements/vitesse-icon';
import '@vitesse/base-ui/elements/inputs/vitesse-select';
import { t, LocaleController } from '@vitesse/base-ui/i18n';
import { encodeBackground, sniffImageType } from '../../dashboards/background-encode';
import type { DashboardBackground } from '../../core/domain/dashboard';

/**
 * <vitesse-dashboard-background-field> — the picture behind a board, chosen once.
 *
 * ONE COMPONENT BECAUSE THERE IS ONE FIELD. Creating a board and editing one offer the same three
 * things, so the picker exists in two dialogs — and a picker copied into two dialogs is the "third
 * way to set the same field" the detail page warned about, with two file inputs, two error surfaces
 * and two chances for one of them to skip the compression. It is written here and used twice.
 *
 * THE PREVIEW IS THE BOARD'S SHAPE, not a thumbnail. A background is judged by how it sits behind
 * tiles, so it is shown at 16:9 with the dim applied live: changing the slider is the only way to
 * answer "will the tiles still read over this", and answering it after saving is answering it too
 * late.
 *
 * Encoding happens on CHOICE, not on save. A picture the browser cannot decode, or one still too
 * large after compression, says so while the person is looking at the picker.
 */
@customElement('vitesse-dashboard-background-field')
export class VitesseDashboardBackgroundField extends LitElement {
  /** The current background, or undefined. Owned by the dialog; this never writes it. */
  @property({ attribute: false }) background?: DashboardBackground;

  @state() private dragging = false;

  constructor() {
    super();
    new LocaleController(this); // re-render on language change
  }

  static styles = css`
    :host { display: block; }
    .field { margin-bottom: 16px; }
    .field:last-child { margin-bottom: 0; }
    label {
      display: block;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 500;
      color: var(--text-primary);
    }
    .help {
      margin-top: 6px;
      font-size: 12px;
      color: var(--text-secondary);
      font-style: italic;
    }

    /* EMPTY: a dropzone, because a picture is a thing you drag. Dashed to read as a target rather
       than as a control that failed to load. */
    .drop {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      aspect-ratio: 16 / 9;
      border: 1px dashed var(--border-color-light);
      border-radius: var(--radius-md);
      background: var(--surface-raised);
      color: var(--text-tertiary);
      font-size: 13px;
      cursor: pointer;
      transition: border-color 0.15s ease, color 0.15s ease;
    }
    .drop:hover, .drop.dragging {
      border-color: var(--blue);
      color: var(--text-secondary);
    }

    /* CHOSEN: the board's own proportions, so what is shown is what the board gets. */
    .preview {
      position: relative;
      aspect-ratio: 16 / 9;
      border-radius: var(--radius-md);
      border: 1px solid var(--border-color);
      background-color: var(--surface-raised);
      background-position: center;
      overflow: hidden;
    }
    /* The dim, rendered exactly as the board renders it — over the picture, under the controls. */
    .preview::after {
      content: '';
      position: absolute;
      inset: 0;
      background: #000;
      opacity: var(--dim, 0);
      pointer-events: none;
    }
    .preview .actions {
      position: absolute;
      inset: auto 8px 8px auto;
      display: flex;
      gap: 6px;
      z-index: 1;
    }

    .controls {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-top: 12px;
    }
    .controls .sub { font-size: 12px; font-weight: 500; color: var(--text-secondary); margin-bottom: 6px; display: block; }
    /* A range, because dim is a judgement made by looking rather than a number anyone knows. */
    input[type='range'] { width: 100%; accent-color: var(--blue); }
    .dimValue { font-variant-numeric: tabular-nums; color: var(--text-tertiary); font-size: 12px; margin-left: 6px; }
    input[type='file'] { display: none; }
  `;

  /** The object URL for the current bytes, revoked when they change: a blob URL pins its bytes. */
  private url = '';
  private urlFor?: Uint8Array;

  private previewUrl(bg: DashboardBackground): string {
    if (this.urlFor === bg.bytes) return this.url;
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = URL.createObjectURL(new Blob([bg.bytes.slice().buffer as ArrayBuffer], { type: bg.mediaType }));
    this.urlFor = bg.bytes;
    return this.url;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.url) URL.revokeObjectURL(this.url);
  }

  private emit(background?: DashboardBackground): void {
    this.dispatchEvent(new CustomEvent('background-change', { detail: { background }, bubbles: true, composed: true }));
  }

  private fail(message: string): void {
    this.dispatchEvent(new CustomEvent('background-error', { detail: { message }, bubbles: true, composed: true }));
  }

  /** Decode, bound and re-encode — the same call the assistant's path makes, so there is one
   *  compression and one set of limits however the picture arrived. */
  private async accept(file: File): Promise<void> {
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const encoded = await encodeBackground(bytes, file.type || sniffImageType(bytes));
      this.emit({
        bytes: encoded.bytes,
        mediaType: encoded.mediaType,
        fit: this.background?.fit ?? 'cover',
        // Enough to hold a tile's text over a photograph, and little enough to leave it a picture.
        dimPercent: this.background?.dimPercent ?? 35,
        prompt: '',
        // EMPTY = a person supplied it, as against a model having invented it. The distinction
        // matters on a screen whose whole claim is that what it shows was measured.
        model: '',
      });
    } catch (e) {
      this.fail(e instanceof Error ? e.message : String(e));
    }
  }

  private pick = () => this.renderRoot.querySelector<HTMLInputElement>('#file')?.click();

  private onDrop = (e: DragEvent) => {
    e.preventDefault();
    this.dragging = false;
    const file = e.dataTransfer?.files?.[0];
    if (file) void this.accept(file);
  };

  render() {
    const bg = this.background;
    return html`<div class="field">
      <label>${t('dashboards.backgroundLabel')}</label>
      ${bg
        ? html`<div
              class="preview"
              style="background-image:url('${this.previewUrl(bg)}');background-size:${bg.fit === 'contain'
                ? 'contain'
                : bg.fit === 'tile'
                  ? 'auto'
                  : 'cover'};background-repeat:${bg.fit === 'tile' ? 'repeat' : 'no-repeat'};--dim:${bg.dimPercent / 100}"
            >
              <div class="actions">
                <vitesse-button color="neutral" size="sm" @click=${this.pick}>${t('dashboards.backgroundReplace')}</vitesse-button>
                <vitesse-button color="red" size="sm" @click=${() => this.emit(undefined)}>${t('dashboards.backgroundRemove')}</vitesse-button>
              </div>
            </div>
            <div class="controls">
              <div>
                <span class="sub">${t('dashboards.backgroundFit')}</span>
                <vitesse-select
                  .options=${[
                    { value: 'cover', label: t('dashboards.backgroundFitCover') },
                    { value: 'contain', label: t('dashboards.backgroundFitContain') },
                    { value: 'tile', label: t('dashboards.backgroundFitTile') },
                  ]}
                  .value=${bg.fit}
                  size="sm"
                  @change=${(e: Event) =>
                    this.emit({ ...bg, fit: (e.target as HTMLSelectElement).value as DashboardBackground['fit'] })}
                ></vitesse-select>
              </div>
              <div>
                <span class="sub">
                  ${t('dashboards.backgroundDim')}<span class="dimValue">${bg.dimPercent}%</span>
                </span>
                <input
                  type="range"
                  min="0"
                  max="90"
                  step="5"
                  .value=${String(bg.dimPercent)}
                  @input=${(e: Event) => this.emit({ ...bg, dimPercent: Number((e.target as HTMLInputElement).value) })}
                />
              </div>
            </div>`
        : html`<div
            class="drop ${this.dragging ? 'dragging' : ''}"
            @click=${this.pick}
            @dragover=${(e: DragEvent) => {
              e.preventDefault();
              this.dragging = true;
            }}
            @dragleave=${() => (this.dragging = false)}
            @drop=${this.onDrop}
          >
            <vitesse-icon name="image-plus" size="20"></vitesse-icon>
            <span>${t('dashboards.backgroundChoose')}</span>
          </div>`}
      <input id="file" type="file" accept="image/png,image/jpeg,image/webp" @change=${(e: Event) => {
        const f = (e.target as HTMLInputElement).files?.[0];
        if (f) void this.accept(f);
        (e.target as HTMLInputElement).value = '';
      }} />
      <p class="help">${t('dashboards.backgroundHint')}</p>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-dashboard-background-field': VitesseDashboardBackgroundField;
  }
}
